/**
 * Input schemas for the `admin.pwaRelease.*` tRPC procedures.
 *
 * Hantavirus ships an Android PWA wrapper. Builds are triggered by
 * superadmins via the dashboard (prompt #24), executed inside a
 * Docker-spawned `hantavirus-android-builder` container (prompt #23),
 * and tracked end-to-end via `pwa_releases` + `pwa_download_log`
 * (prompt #20).
 *
 * These schemas live in `@hantavirus/shared` rather than `@hantavirus/api`
 * because the web app's tRPC client also needs the input types for
 * superadmin forms. The original prompt referenced `packages/server`
 * but Hantavirus uses `packages/shared` as the cross-cutting types
 * package.
 *
 * The semver regex matches the Android constraint: three dot-separated
 * non-negative integers. Pre-release/build-metadata suffixes (`-rc.1`,
 * `+build.42`) are rejected because Android `versionName` does not
 * support them in our distribution flow.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Primitive sub-schemas
// ---------------------------------------------------------------------------

export const PWA_VERSION_REGEX = /^\d+\.\d+\.\d+$/;

// Zod's `.regex()` `message` is a literal string surfaced to clients
// when validation fails; tRPC wraps it in a BAD_REQUEST. The web app
// translates with the `errors.pwa.versionInvalid` key — see
// apps/api/src/lib/i18n.ts (en/ar/fr).
export const pwaVersionSchema = z
  .string()
  .regex(PWA_VERSION_REGEX, {
    message: "Version must be semver (e.g. 1.0.1)",
  });

/** Markdown allowed, but the row caps changelog at 10 000 chars. */
export const pwaChangelogSchema = z.string().max(10_000);

export const pwaVersionCodeSchema = z
  .number()
  .int()
  .positive()
  .max(2_100_000_000);

export const pwaStatusSchema = z.enum([
  "building",
  "ready",
  "failed",
  "archived",
]);
export type PwaStatus = z.infer<typeof pwaStatusSchema>;

// ---------------------------------------------------------------------------
// Procedure inputs — match the 11 procedures in apps/api/src/routers/admin/
// pwa-release.ts. Each export is named `pwa<Verb>InputSchema` for
// consistency with the rest of the @hantavirus/shared barrel.
// ---------------------------------------------------------------------------

export const pwaCreateReleaseInputSchema = z.object({
  version: pwaVersionSchema,
  versionCode: pwaVersionCodeSchema,
  changelog: pwaChangelogSchema.optional().default(""),
});
export type PwaCreateReleaseInput = z.infer<typeof pwaCreateReleaseInputSchema>;

export const pwaListInputSchema = z
  .object({
    status: pwaStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).optional().default(50),
    offset: z.number().int().min(0).optional().default(0),
  })
  .optional()
  .default({});
export type PwaListInput = z.infer<typeof pwaListInputSchema>;

export const pwaArchiveInputSchema = z.object({
  releaseId: z.string().min(1),
});
export type PwaArchiveInput = z.infer<typeof pwaArchiveInputSchema>;

export const pwaBuildStatusInputSchema = z.object({
  releaseId: z.string().min(1),
});
export type PwaBuildStatusInput = z.infer<typeof pwaBuildStatusInputSchema>;

export const pwaBuildLogsInputSchema = z.object({
  releaseId: z.string().min(1),
  /** Byte offset for paginated tail reads of large logs. */
  offset: z.number().int().min(0).optional().default(0),
  /** Page size in characters, capped to 256 KiB. */
  limit: z.number().int().min(1).max(262_144).optional().default(65_536),
});
export type PwaBuildLogsInput = z.infer<typeof pwaBuildLogsInputSchema>;

export const pwaBuildRetryInputSchema = z.object({
  releaseId: z.string().min(1),
});
export type PwaBuildRetryInput = z.infer<typeof pwaBuildRetryInputSchema>;

export const pwaBuildCancelInputSchema = z.object({
  releaseId: z.string().min(1),
});
export type PwaBuildCancelInput = z.infer<typeof pwaBuildCancelInputSchema>;

export const pwaStatsRangeInputSchema = z
  .object({
    days: z.number().int().min(1).max(365).optional().default(30),
    version: z.string().optional(),
  })
  .optional()
  .default({});
export type PwaStatsRangeInput = z.infer<typeof pwaStatsRangeInputSchema>;

// ---------------------------------------------------------------------------
// Output shapes — kept loose (z.unknown for embedded JSON) because the
// admin UI types its components from `inferRouterOutputs<AppRouter>` in
// the web app, not from these output schemas.
// ---------------------------------------------------------------------------

export const pwaBuildProgressSchema = z.object({
  /** Build pipeline stage. */
  stage: z.enum(["validate", "bubblewrap", "gradle", "sha256", "upload"]),
  /** Percent complete within stage, 0-100. */
  pct: z.number().int().min(0).max(100),
  /** Optional human-readable log line for the UI. */
  message: z.string().optional(),
});
export type PwaBuildProgress = z.infer<typeof pwaBuildProgressSchema>;

export const pwaDiagnoseCheckSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  /** Optional latency in ms; helps the UI surface slow Redis/Docker. */
  latencyMs: z.number().int().min(0).optional(),
});
export type PwaDiagnoseCheck = z.infer<typeof pwaDiagnoseCheckSchema>;

export const pwaDiagnoseResultSchema = z.object({
  redis: pwaDiagnoseCheckSchema,
  worker: pwaDiagnoseCheckSchema,
  dockerSocket: pwaDiagnoseCheckSchema,
  builderImage: pwaDiagnoseCheckSchema,
  allPassed: z.boolean(),
});
export type PwaDiagnoseResult = z.infer<typeof pwaDiagnoseResultSchema>;
